// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Clickable from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import LazyImage from "@insite/mobius/LazyImage";
import { injectCss } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { previouslyPurchasedTagId, topSellerTagId } from "@pacific/App/Hooks/xUseWidgetBadges";
import PrevPurchase from "@pacific/XDL/Icons/PrevPurchase";
import TopSelling from "@pacific/XDL/Icons/TopSelling";
import { color, ellipsis } from "@pacific/XDL/Utilities";
import * as React from "react";
import styled, { css } from "styled-components";

export enum BadgeTypeValues {
    Image = "Image",
    Text = "Text",
}

export interface BadgePresentationProps {
    textBadgeCss?: InjectableCss;
    imageBadgeProps?: JSX.IntrinsicElements["img"];
}

const TextBadge = styled.div<{
    backgroundColor: string;
    color: string;
    badgeTextRounding: number;
    badgeBorderColor: string;
}>`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px 8px;
    background-color: #${props => props.backgroundColor};
    color: #${props => props.color};
    border: 1px solid #${props => color(props.badgeBorderColor)};
    border-radius: ${props => props.badgeTextRounding}px;
    width: 100%;
`;

const TextBadgeText = styled.span<InjectableCss>`
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0;
    line-height: 13px;
    word-break: break-all;
    ${ellipsis(2)}
    ${injectCss}
`;

export type BadgeProps = {
    /** When type is Image an img tag will be rendered with the src being the value prop.
     * When the type is text a textbox will be rendered with the given value used as text and color scheme
     * denoted in badgeTextColor and badgeBackgroundColor
     * When no type is given or no corresponding value is given, nothing will render.
     */
    type: BadgeTypeValues;
    /** URL image path or badge text */
    value?: string;
    /** Alt text for the image badge */
    imageAltText?: string;
    /** Hex code for badge text color
     * Default is white (FFFFFF)
     */
    badgeTextColor?: string;
    /** Hex code for badge background color
     * Default is black (000000)
     */
    badgeBackgroundColor?: string;
    /** Number used to determine border radius for text badge in pixels
     * Default is 0
     */
    badgeTextRounding?: number;
    // XNG-Changes
    badgeBorderColor?: string;
    badgeTag?: string;
} & BadgePresentationProps;

interface BadgeStyles {
    prevPurchaseIcon?: IconPresentationProps;
    topSellingIcon?: IconPresentationProps;
}

export const badgeStyles: BadgeStyles = {
    prevPurchaseIcon: {
        css: css`
            width: 88px;
            height: 21px;
            svg {
                width: 88px;
                height: 21px;
            }
        `,
    },
    topSellingIcon: {
        css: css`
            width: 71px;
            height: 21px;
            svg {
                width: 71px;
                height: 21px;
            }
        `,
    },
};


const Badge = ({
    type,
    value,
    imageAltText,
    badgeTextColor,
    badgeBackgroundColor,
    badgeTextRounding,
    imageBadgeProps,
    textBadgeCss,
    badgeBorderColor,
    badgeTag,
}: BadgeProps) => {
    const styles = badgeStyles;

    if (!type || !value) {
        return null;
    }

    const { recentlyPurchasedPageUrl } = useAppSelector(
        state => ({
            recentlyPurchasedPageUrl: getPageLinkByPageType(state, "RecentlyPurchasedProductsPage")?.url,
        }),
    );

    if(badgeTag === previouslyPurchasedTagId) {
        const imgComponent = recentlyPurchasedPageUrl ? (
            <Clickable href={recentlyPurchasedPageUrl}>
                <Icon  {...styles.prevPurchaseIcon} src={PrevPurchase} />
            </Clickable>
        ) : (
            <Icon  {...styles.prevPurchaseIcon} src={PrevPurchase} />
        );
        return imgComponent;
    }

    if(badgeTag === topSellerTagId) {
        return (
            <Icon {...styles.topSellingIcon} src={TopSelling} />
        );
    }

    if (type === BadgeTypeValues.Image) {
        return (<LazyImage imgProps={imageBadgeProps} src={value} altText={imageAltText} />);
    }

    if (type === BadgeTypeValues.Text) {
        return (
            <TextBadge
                backgroundColor={badgeBackgroundColor || "CCCCCC"}
                color={badgeTextColor || "3B3B3B"}
                badgeTextRounding={badgeTextRounding || 0}
                badgeBorderColor={badgeBorderColor || "CCCCCC"}
            >
                <TextBadgeText {...textBadgeCss}>{value}</TextBadgeText>
            </TextBadge>
        );
    }

    // XNG-Changes: add translate
    return <>{translate("Unknown badge type")}</>;
};

/** @component */
export default Badge;
